import React, { useState, useRef, useEffect } from "react";
import "./CaseStudiesPageContent.css";
import "./CaseStudies.css";
import "../pages/EnterpriseSoftwarePage.css";
import {
  ArrowRight, ChevronDown
} from "lucide-react";
import { Link } from "react-router-dom";
import Button from "../components/Button";

import globalSupplyChainImg from "../assets/Global_supply_chain_ ERP.webp";
import databaseMigrationImg from "../assets/Database_migrationjpg.webp";
import legalContractImg from "../assets/legal contract.webp";

import serviceIcon from '../assets/industries/Related Pages/Service.webp';
import solutionIcon from '../assets/industries/Related Pages/solution.webp';
import productIcon from '../assets/industries/Related Pages/product.webp';
import contactUsIcon from '../assets/industries/Related Pages/contact-us.webp';

interface CaseStudiesProps {
  preview?: boolean;
}

const categoryConfig: Record<string, { bg: string; color: string; accent: string }> = {
  "Enterprise ERP": { bg: "rgba(139,92,246,0.15)", color: "#a78bfa", accent: "#8b5cf6" },
  "AI & Diagnostics": { bg: "rgba(20,184,166,0.15)", color: "#2dd4bf", accent: "#14b8a6" },
  "Industrial Automation": { bg: "rgba(245,158,11,0.15)", color: "#fbbf24", accent: "#f59e0b" },
  "Cloud Architecture": { bg: "rgba(99,102,241,0.15)", color: "#818cf8", accent: "#6366f1" },
  "Workflow Automation": { bg: "rgba(244,63,94,0.15)", color: "#fb7185", accent: "#f43f5e" },
};

const CountUp: React.FC<{ value: string; isVisible: boolean }> = ({ value, isVisible }) => {
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!isVisible) return;

    const match = value.match(/^([+\-]?)(\d+(?:\.\d+)?)(.*)/);
    if (!match) {
      setDisplay(value);
      return;
    }

    const prefix = match[1];
    const target = parseFloat(match[2]);
    const suffix = match[3];
    let start = 0;
    const duration = 1200;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress * (2 - progress);
      const current = start + eased * (target - start);

      if (Number.isInteger(target)) {
        setDisplay(`${prefix}${Math.floor(current)}${suffix}`);
      } else {
        setDisplay(`${prefix}${current.toFixed(1)}${suffix}`);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, isVisible]);

  return <span>{display}</span>;
};

const caseList = [
  {
    category: "Enterprise ERP",
    title: "How We Helped a Japan-Based Manufacturer Transform Complex Operations with ERP",
    desc: "A custom ERP platform helped the company streamline workflows, connect business functions, and gain real-time operational visibility.",
    points: [
      "Unified production and inventory processes",
      "Automated manual workflows",
      "Improved business visibility",
      "Enabled scalable growth",
    ],
    metrics: [] as { label: string; value: string }[],
    link: "/case-studies/erp-transformation-japan-manufacturing",
  },
  {
    category: "AI & Diagnostics",
    title: "How We Transformed Dental Diagnostics with AI in Chennai",
    desc: "Discover how Vivify Soft Tech helped a Chennai-based dental provider improve diagnostic accuracy, automate image analysis, and streamline clinical workflows with an AI-powered solution.",
    points: [
      "AI-powered dental diagnostics",
      "Faster clinical decision-making",
      "Automated image analysis",
      "Improved patient care",
    ],
    metrics: [] as { label: string; value: string }[],
    link: "/case-studies/ai-powered-dental-diagnostics-chennai",
  },
  {
    category: "Industrial Automation",
    title: "Transforming Factory Floors with Intelligent Automation",
    desc: "Discover how Vivify Soft Tech helped a Tamil Nadu manufacturer replace manual operations with intelligent automation, enabling real-time production visibility, smarter workflows, and greater operational efficiency.",
    points: [
      "Smart factory transformation",
      "Real-time production intelligence",
      "Connected industrial operations",
      "Higher productivity and efficiency",
    ],
    metrics: [] as { label: string; value: string }[],
    link: "/case-studies/industrial-automation-tamil-nadu",
  },
  {
    category: "Cloud Architecture",
    title: "Where Scalable Cloud Architecture Begins",
    desc: "Discover how Vivify Soft Tech transformed legacy infrastructure into a secure, scalable cloud environment that improved performance and supported long-term business growth.",
    points: [
      "Scalable cloud infrastructure",
      "Secure cloud architecture",
      "Optimized system performance",
      "Future-ready business growth",
    ],
    metrics: [] as { label: string; value: string }[],
    link: "/case-studies/scalable-cloud-architecture",
  },
  {
    category: "Workflow Automation",
    title: "The Power Behind Seamless Workflows",
    desc: "Discover how Vivify Soft Tech helped a growing enterprise automate business processes, streamline approvals, and improve operational efficiency with a customized workflow automation solution.",
    points: [
      "Intelligent workflow automation",
      "Faster approvals and task management",
      "Connected business processes",
      "Improved operational efficiency",
    ],
    metrics: [] as { label: string; value: string }[],
    link: "/case-studies/seamless-workflow-automation",
  },
];

const caseImages = [
  globalSupplyChainImg,
  "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80",
  databaseMigrationImg,
  legalContractImg,
];

const faqs = [
  {
    q: "What types of projects can Vivify build?",
    a: "Vivify develops custom software solutions including ERP systems, mobile apps, AI platforms, cloud applications, IoT dashboards, and enterprise software. We build scalable solutions tailored to your business goals.",
  },
  {
    q: "Does Vivify build ERP and workflow platforms?",
    a: "Yes, we create custom ERP, HRMS, CRM, and workflow automation systems that streamline operations and improve productivity. Our platforms are designed to fit your unique business processes.",
  },
  {
    q: "Can Vivify build mobile apps?",
    a: "Absolutely. We develop secure and user-friendly mobile applications for Android, iOS, and cross-platform environments. Our apps are built for performance, scalability, and seamless user experiences.",
  },
  {
    q: "Does Vivify work on industrial automation projects?",
    a: "Yes, we deliver industrial automation solutions with IoT integration, real-time monitoring, predictive maintenance, and smart factory technologies. These systems help businesses improve efficiency and reduce operational downtime.",
  },
  {
    q: "How can I start a similar project with Vivify?",
    a: "Simply contact our team and share your requirements or project idea. We'll discuss your goals, define the scope, and create a customized roadmap for development and deployment.",
  },
];

const relatedPages = [
  { iconName: "Layers", title: "Services", desc: "Enterprise software, AI solutions and custom development", link: "/services" },
  { iconName: "Package", title: "Products", desc: "Factorai, Communai, Smilai and Securai platforms", link: "/products" },
  { iconName: "Zap", title: "Solutions", desc: "Industry-specific technology solution bundles", link: "/solutions" },
  { iconName: "Factory", title: "Contact Us", desc: "Share your project idea and get started today", link: "/contact" },
];

const relatedIconMap: Record<string, React.ReactNode> = {
  Layers: <img loading="lazy" src={serviceIcon} alt="Services" className="related-inline-icon" style={{ width: 20, height: 20, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
  Package: <img loading="lazy" src={productIcon} alt="Products" className="related-inline-icon" style={{ width: 20, height: 20, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
  Zap: <img loading="lazy" src={solutionIcon} alt="Solutions" className="related-inline-icon" style={{ width: 20, height: 20, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
  Factory: <img loading="lazy" src={contactUsIcon} alt="Contact Us" className="related-inline-icon" style={{ width: 20, height: 20, objectFit: 'contain' }}  title="Vivify Soft Tech" />,
};

const CategoryCard: React.FC<{ card: typeof caseList[0]; idx: number }> = ({ card, idx }) => {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const imageUrl = caseImages[idx] || caseImages[0];
  const cfg = categoryConfig[card.category] ?? { bg: "rgba(255,255,255,0.08)", color: "#a1a1aa", accent: "#71717a" };

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        io.disconnect();
      }
    }, { threshold: 0.1, rootMargin: "0px 0px -60px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Link
      ref={cardRef}
      to={card.link}
      className={`case-card-stacked ${isVisible ? "active" : ""}`}
      style={{
        "--card-index": idx,
        "--card-accent": cfg.accent,
        "--card-color": cfg.color,
        "--card-bg": cfg.bg,
        transitionDelay: `${idx * 0.05}s`,
      } as React.CSSProperties}
      aria-label={`View case studies: ${card.title}`}
    >
      <div className="case-card-image-section">
        <img 
          loading={idx === 0 ? "eager" : "lazy"} 
          fetchPriority={idx === 0 ? "high" : "auto"}
          src={imageUrl} 
          alt={card.title} 
          className="case-card-img"  
          title="Vivify Soft Tech" 
        />
      </div>

      <div className="case-card-info-section">
        <div className="case-card-header-line">
          <div className="case-card-header-inner">
            <h3 className="case-card-title">{card.title}</h3>
            <span className="case-card-category" style={{ color: cfg.color }}>
              {card.category}
            </span>
          </div>
        </div>

        <p className="case-card-desc">{card.desc}</p>

        {card.points.length > 0 ? (
          <ul className="case-card-points">
            {card.points.map((point, pIdx) => (
              <li key={pIdx}>{point}</li>
            ))}
          </ul>
        ) : (
          <div className="case-card-metrics-row">
            {card.metrics.map((met, mIdx) => (
              <div key={mIdx} className="metric-badge-box">
                <span className="metric-badge-value">
                  <CountUp value={met.value} isVisible={isVisible} />
                </span>
                <span className="metric-badge-label">{met.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};

export const CaseStudiesPageContent: React.FC<CaseStudiesProps> = ({ preview = false }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (preview) {
    return (
      <section className="section cs-preview-section" id="case-studies">
        <div className="container">
          <div className="section-header centered">
            <h2 className="section-title">
              Project <span className="text-gradient-purple">Capabilities</span>
            </h2>
            <p className="section-subtitle">
              Explore the types of projects Vivify can build across ERP, AI, automation, mobile, and cloud.
            </p>
          </div>
          <div className="case-studies-stacked-list">
            {caseList.slice(0, 5).map((card, idx) => (
              <CategoryCard key={idx} card={card} idx={idx} />
            ))}
          </div>
          <div className="centered" style={{ marginTop: "3rem" }}>
            <Link to="/case-studies">
              <Button variant="primary" size="md" icon={<ArrowRight size={16} />}>
                View All Case Studies
              </Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <main className="cs-page" style={{ paddingTop: "80px" }}>
      {(() => { document.title = "Case Studies | AI, ERP, Cloud, IoT, Automation & Mobile App Projects"; return null; })()}

      {/* ─── Hero Section (matches SecurAI / enterprise-hero style) ─── */}
      <section
        className="enterprise-hero"
        aria-label="Case Studies Hero"
        style={{ position: 'relative' }}
      >
        <img 
          src="/CaseStudies.webp" 
          alt="" 
          fetchPriority="high" 
          style={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'center right',
            zIndex: 0
          }}
        />
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(75deg, rgba(5, 9, 21, 0.85) 0%, rgba(5, 9, 21, 0.85) 60%, rgba(5, 9, 21, 0) 60.1%, rgba(5, 9, 21, 0) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="enterprise-hero-bg" style={{ zIndex: 2, position: 'relative', pointerEvents: 'none' }}>
          <div className="enterprise-hero-grid" />
          <div className="enterprise-hero-orb-1" style={{ opacity: 0.5 }} />
          <div className="enterprise-hero-orb-2" style={{ opacity: 0.5 }} />
        </div>

        <div className="container enterprise-hero-inner" style={{ position: 'relative', zIndex: 3 }}>
          <div className="enterprise-hero-content cs-enterprise-hero-content">
            <h1 className="enterprise-hero-h1">
              Building Intelligent Solutions That{" "}
              <span className="accent">Transform Businesses</span>
            </h1>

            <p className="enterprise-hero-meta">
              Explore a collection of high-impact software solutions engineered to streamline operations,
              enhance productivity, and unlock new opportunities through AI, automation, cloud computing,
              and enterprise technologies.
            </p>

            <div className="enterprise-hero-actions">
              <Link to="/contact" className="enterprise-hero-primary-btn">
                Start a Project <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="enterprise-hero-primary-btn">
                Explore Services <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="cs-cards-section">
        <div className="container">
          <div className="section-header centered">
            <h2 className="section-title">
              What We Can <span className="text-gradient-purple">Build for You</span>
            </h2>
            <p className="section-subtitle">
              Browse our project categories covering the systems, platforms, and applications
              Vivify can design, build, and deploy for your business.
            </p>
          </div>
          <div className="case-studies-stacked-list">
            {caseList.slice(0, 5).map((card, idx) => (
              <CategoryCard key={idx} card={card} idx={idx} />
            ))}
          </div>
        </div>
      </section>


      <section className="cs-faq-section">
        <div className="container">
          <div className="section-header centered">
            <h2 className="section-title">
              Frequently <span className="text-gradient-purple">Asked Questions</span>
            </h2>
            <p>Common questions about Vivify project capabilities and how to get started.</p>
          </div>
          <div className="faq-accordion-container">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`faq-accordion-item${openFaq === i ? " open" : ""}`}
              >
                <button
                  className="faq-trigger-btn"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={18} className="faq-trigger-chevron" />
                </button>
                <div className="faq-content-pane">
                  <div className="faq-content-inner">{faq.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cs-related-section">
        <div className="container">
          <div className="section-header centered">
            <h2 className="section-title">
              Related <span className="text-gradient-purple">Pages</span>
            </h2>
            <p>Discover more about Vivify services, solutions, and products.</p>
          </div>
          <div className="cs-related-grid">
            {relatedPages.map((page, idx) => (
              <Link key={idx} to={page.link} className="related-page-card panel-card panel-hover">
                <h3 className="related-page-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  {relatedIconMap[page.iconName]}
                  {page.title}
                </h3>
                <p className="related-page-desc">{page.desc}</p>
                <span className="related-page-arrow"><ArrowRight size={16} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="about-cta-section cs-cta-section">
        <div className="about-cta-glow" />
        <div className="container about-cta-inner">
          <h2 className="about-cta-title" style={{ fontWeight: 400 }}>
            Ready to See Real-World Success Stories?
          </h2>
          <p className="about-cta-desc">
            Discover how our Industrial IoT solutions have helped businesses improve efficiency, reduce downtime, and accelerate digital transformation.
          </p>
          <div className="about-cta-actions">

            <Link to="/contact">
              <Button variant="primary" size="lg" icon={<ArrowRight size={16} />}>
                Request Demo
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="primary" size="lg" icon={<ArrowRight size={16} />}>
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CaseStudiesPageContent;
